import { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router'
import { useSuspenseQuery } from '@tanstack/react-query'

import { ThemeProvider, useTheme } from '@mui/material/styles'

import API from '@shared/services/src/API'
import { QK } from '@shared/utils'

import { useThemeArgs } from './WhiteLabeling.hooks'
import { themeUpdater } from './WhiteLabeling.utils'

/**
 * White labeling feature is used to customize the app for different clients.
 * This component wraps the whole app and gets the theme from the BE to use it.
 * Until the theme is fetched, the app is not rendered to avoid flickering.
 */
export default function ThemeGuard({ children }) {
  const path = useLocation().pathname

  const { fa, token, theme_id } = useThemeArgs()

  const [queryParams] = useState(() => {
    if (path.startsWith('/auth/enrollment') && theme_id) return { theme_id }
    if (path.startsWith('/auth/sent') && theme_id) return { theme_id }
    if (path.startsWith('/auth/code') && theme_id) return { theme_id }
    if (path.startsWith('/auth/verify') && fa) return { token: fa }
    if (path.startsWith('/auth/verified') && token) return { token }
    return undefined
  })

  const { data } = useSuspenseQuery({
    queryKey: QK.theme.details(queryParams),
    // Ignore theme error, it is not critical
    queryFn: () => API.theme.details(queryParams, { skipHandling: true }).catch(() => null),
    select: (data) => (data ? themeUpdater(data.attributes) : null),
    staleTime: 60 * 60 * 1000,
  })

  if (!data) return children
  return <WhiteLabeling theme={data}>{children}</WhiteLabeling>
}

export function WhiteLabeling({ theme, children }) {
  return (
    <ThemeProvider theme={theme}>
      {children}
      <Overrides />
    </ThemeProvider>
  )
}

// Override index.html tags according to the current theme
function Overrides() {
  const theme = useTheme()

  const manifest = useMemo(() => {
    const content = encodeURIComponent(
      JSON.stringify({
        name: theme.app.name,
        short_name: theme.app.name,
        start_url: import.meta.env.VITE_URL,
        display: 'standalone',
        theme_color: '#000000',
        background_color: '#ffffff',
        icons: [
          {
            src: theme.app.default ? `${import.meta.env.VITE_URL}/logo-300x298.png` : theme.app.logo.main,
            type: 'image/png',
            sizes: '192x192',
          },
        ],
      })
    )
    return 'data:application/manifest+json,' + content
  }, [theme.app.default, theme.app.logo.main, theme.app.name])

  return (
    <Helmet>
      <title>{theme.app.name}</title>
      <meta name="title" content={theme.app.name} />
      <meta
        name="description"
        content={`PrEP is a once-daily pill that is 99% effective at preventing HIV. ${theme.app.name} makes getting PrEP easy with virtual doctor visits, at-home labs, and your prescriptions delivered directly to your door. Do it all online from the comfort of your home.`}
      />

      {/* Font */}
      {theme.typography.url && <link id="custom-font" rel="stylesheet" href={theme.typography.url} />}

      {/* Manifest with modified pictures */}
      <link rel="manifest" href={manifest} />

      {/* Apple icon */}
      <link rel="apple-touch-icon" href={`${theme.app.default ? '/logo-300x298.png' : theme.app.logo.favicon}?v=2`} />

      {/* Favicon */}
      <link rel="icon" href={`${theme.app.default ? '/favicon.png' : theme.app.logo.favicon}?v=2`} sizes="any" />
    </Helmet>
  )
}
